import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DOT, laneStage, nowOf } from "@/lib/stages";
import { ukDayTime } from "@/lib/time";
import { StatusChip } from "./kit";

const letter = (d, i) => (d.alias || `Desk ${String.fromCharCode(65 + i)}`).split(" ").pop();

/** What one desk has said, in a line the introducer can read without opening anything. */
export function said(d) {
  const st = laneStage(d);
  if (d.withdrawn) return `We took it back — ${(d.withdrawn_reason || "no decision in time").toLowerCase()}`;
  if (d.status === "declined") return d.decline_reason || "Declined — no reason given";
  const n = nowOf(d);
  if (n) return `Right now: ${n.key === "callback" ? `call back booked for ${ukDayTime(n.callback_at, "a set time")}` : n.label.toLowerCase()} · next: ${n.next.toLowerCase()}`;
  if (st.key === "offer_accepted") return "Your client accepted the offer — funds being arranged";
  if (st.key === "offer_declined") return "Your client turned the offer down — the desk can come back with new terms";
  if (d.status === "offer_made" && d.offer) {
    return [d.offer.rate_pct != null ? `${d.offer.rate_pct}%` : null, d.offer.term_months ? `${d.offer.term_months} months` : null]
      .filter(Boolean).join(" over ") || "Terms issued";
  }
  if (d.status === "offer_made") return "Terms with your client";
  if (st.key === "in_progress") return "Working on it now";
  if (d.status === "in_review") return d.chases ? `Reading the pack · chased ${d.chases}×` : "Reading the pack";
  return d.chases ? `Not opened yet · chased ${d.chases}×` : "Not opened yet";
}

/**
 * One quiet line — "A Under assessment · B Offer issued" — that opens into what each desk has said.
 * Desks are only ever letters, fixed per case.
 */
export function DeskLetters({ desks, testId = "desk-letters" }) {
  if (!desks?.length) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="desk-line" data-testid={testId} onClick={(e) => e.stopPropagation()}
                title="See what each desk has said">
          {desks.map((d, i) => {
            const st = laneStage(d);
            return (
              <span className="dl" key={d.alias || i}>
                <i className={st.live ? `live t-${st.tone}` : undefined} style={{ background: DOT[st.tone] || DOT.slate }} />
                <b>{letter(d, i)}</b>{st.label}
              </span>
            );
          })}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-3 desk-pop" data-testid={`${testId}-pop`} onClick={(e) => e.stopPropagation()}>
        <div style={{ font: '700 10.5px "Instrument Sans"', letterSpacing: ".14em", textTransform: "uppercase", color: "#64748B", padding: "2px 2px 6px" }}>
          What each desk has said
        </div>
        {desks.map((d, i) => (
          <div className="dp-row" key={d.alias || i} data-testid={`${testId}-row-${i}`}>
            <div className="dp-av">{letter(d, i)}</div>
            <div style={{ minWidth: 0 }}>
              <div className="cxp" style={{ background: "transparent" }}>
                <StatusChip state={laneStage(d)} />
              </div>
              <div className="dp-said">{said(d)}</div>
              <div className="dp-at" data-testid={`${testId}-move-${i}`}>
                {d.move ? `${d.move.text} · ${ukDayTime(d.move.at, "—")}`
                  : `Last movement ${ukDayTime(d.step_at || d.status_updated_at || d.last_worked_at || d.sent_at, "—")}`}
              </div>
            </div>
          </div>
        ))}
        <div style={{ fontSize: 11.5, color: "#7C8AA0", padding: "8px 2px 0" }}>Desk letters are fixed for this case and never name the lender.</div>
      </PopoverContent>
    </Popover>
  );
}

export default DeskLetters;
